import { message } from 'ant-design-vue';
import axios, { type AxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types/api';

const LOGIN_PATH = '/user/login';
const DEMO_BUILD = import.meta.env.MODE === 'demo';
export const DEMO_READ_ONLY_CODE = 40301;
export const DEMO_READ_ONLY_ERROR = 'demo_read_only';
export const DEMO_READ_ONLY_MESSAGE = '演示环境为只读模式，此操作不可用';
const DEMO_READ_ONLY_MESSAGE_KEY = 'film-demo-read-only';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const DEMO_AUTH_MUTATION_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/logout',
]);

export function shouldBlockDemoMutation(
  config: Pick<AxiosRequestConfig, 'method' | 'url'>,
  demoBuild = DEMO_BUILD,
): boolean {
  if (!demoBuild) return false;
  const method = String(config.method || 'GET').toUpperCase();
  if (SAFE_METHODS.has(method)) return false;
  const path = String(config.url || '').split(/[?#]/, 1)[0]?.replace(/\/$/, '');
  return !DEMO_AUTH_MUTATION_PATHS.has(path || '');
}

function recordValue(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

export function isDemoReadOnlyPayload(value: unknown): boolean {
  const payload = recordValue(value);
  return (
    Number(payload?.code || 0) === DEMO_READ_ONLY_CODE ||
    payload?.error === DEMO_READ_ONLY_ERROR
  );
}

export function isDemoReadOnlyResponseError(value: unknown): boolean {
  const error = recordValue(value);
  const response = recordValue(error?.response);
  return isDemoReadOnlyPayload(response?.data);
}

function notifyDemoReadOnly() {
  message.warning({
    key: DEMO_READ_ONLY_MESSAGE_KEY,
    content: DEMO_READ_ONLY_MESSAGE,
  });
}

export class ApiError extends Error {
  readonly code?: number;
  readonly status?: number;

  constructor(messageText: string, options?: { code?: number; status?: number }) {
    super(messageText);
    this.name = 'ApiError';
    this.code = options?.code;
    this.status = options?.status;
  }
}

const client = axios.create({
  baseURL: '/',
  timeout: 30_000,
});

client.interceptors.request.use((config) => {
  if (DEMO_BUILD && shouldBlockDemoMutation(config, true)) {
    notifyDemoReadOnly();
    throw new ApiError(DEMO_READ_ONLY_MESSAGE, {
      code: DEMO_READ_ONLY_CODE,
      status: 403,
    });
  }
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error instanceof ApiError &&
      error.code === DEMO_READ_ONLY_CODE
    ) {
      return Promise.reject(error);
    }
    const status = Number(error?.response?.status || 0);
    const businessCode = Number(error?.response?.data?.code || 0);
    const detail =
      error?.response?.data?.message || error?.message || '请求失败，请重试';

    if (isDemoReadOnlyResponseError(error)) {
      notifyDemoReadOnly();
      return Promise.reject(
        new ApiError(DEMO_READ_ONLY_MESSAGE, {
          code: DEMO_READ_ONLY_CODE,
          status: status || 403,
        }),
      );
    }

    if (status === 401 || status === 403 || businessCode === 401 || businessCode === 403) {
      localStorage.removeItem('token');
      if (window.location.pathname !== LOGIN_PATH) {
        message.error('登录已过期，请重新登录');
        const redirect = `${window.location.pathname}${window.location.search}`;
        window.location.assign(`${LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`);
      }
    } else if (status === 429 && businessCode === 770004) {
      message.warning(detail || '115 访问已达到上限，请稍后重试');
    }

    return Promise.reject(
      new ApiError(detail, {
        code: businessCode || undefined,
        status: status || undefined,
      }),
    );
  },
);

export async function request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const response = await client.request<ApiResponse<T>>(config);
  const envelope = response.data;
  if (typeof envelope?.code === 'number' && envelope.code !== 0) {
    if (isDemoReadOnlyPayload(envelope)) {
      notifyDemoReadOnly();
      throw new ApiError(DEMO_READ_ONLY_MESSAGE, {
        code: DEMO_READ_ONLY_CODE,
        status: 403,
      });
    }
    if (envelope.code === 401 || envelope.code === 403) {
      localStorage.removeItem('token');
      if (window.location.pathname !== LOGIN_PATH) {
        message.error('登录已过期，请重新登录');
        const redirect = `${window.location.pathname}${window.location.search}`;
        window.location.assign(
          `${LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`,
        );
      }
    }
    throw new ApiError(envelope.message || '请求失败', { code: envelope.code });
  }
  return envelope;
}
